import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { ApiHelper } from "@datanac/datanac-api-toolkit/dist/api-helper";
import { useDialog } from "@datanac/datanac-mui-toolkit/dist/hooks";
import { AddCircleOutline, SearchOutlined } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Button, Dialog, Grid, IconButton, InputAdornment, TextField } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { _sessionManager } from "api/ApiHelper";
import { AppContext } from "App";
import WorkspaceActionBar from "components/Workspaces/WorkspaceActionBar";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import { USER_ROLE_ADMIN } from "core/useSession";
import { enqueueSnackbar } from "notistack";
import { v4 } from "uuid";

import { useChatAssistantAttachments } from "../../contexts/useChatAssistantAttachments";
import { EditIcon, NavigateIcon, SystemIcon } from "../Icons/Icons";
import TilleyDataGrid from "../shared/DataGrid/TilleyDataGrid";
import { AgencyAddEditDialog } from "./AgencyAddEditDialog";

export default function AgencyListWorkspace() {
  const { globalState } = useContext(AppContext);
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isAddEditAgency, setAddEditAgency] = useState(null);
  const navigate = useNavigate();
  const { component: confirmDialogComponent, actions: confirmDialogActions } = useDialog();
  const { addAttachments } = useChatAssistantAttachments();

  const getApi = useCallback(() => {
    return new ApiHelper({
      baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
      config: {},
      sessionManager: _sessionManager,
      _instance: null,
    });
  }, []);

  const responseMessage = (err) => {
    const detail = err?.response?.data?.detail || err?.detail || "";
    const status = err?.response?.status;
    const messageFromServer = err?.message || "";

    if (detail.includes("Cannot insert duplicate key")) {
      return "Duplicate entry: This agent already exists.";
    }

    if (status === 500) {
      return "Server error. Please contact support.";
    }

    if (messageFromServer) {
      return messageFromServer;
    }

    return "Something went wrong. Please try again.";
  };

  const handleNotificationMessage = (message, variant) => {
    const _message = variant === "error" ? responseMessage(message) : message;
    enqueueSnackbar(_message, { preventDuplicate: true, variant });
  };

  const loadAgencies = useCallback(async () => {
    setIsLoading(true);
    try {
      const api = getApi();
      const filter = {
        is_active: true,
        ...(globalState.agent_token ? { agent_token: globalState.agent_token } : {}),
      };
      const agencies = await api.callPost("Agency/Search", filter, globalState);
      setRows(agencies);
      return agencies;
    } catch (err) {
      handleNotificationMessage(err, "error");
    } finally {
      setIsLoading(false);
    }
  }, [getApi, globalState]);

  useEffect(() => {
    loadAgencies();
  }, [loadAgencies]);

  const AddUpdateAgency = useCallback(
    async (updates) => {
      try {
        const api = getApi();
        const isNew = !updates.id;
        if (isNew) {
          updates.id = v4();
        }
        if (updates.is_active === undefined) {
          updates.is_active = true;
        }
        await api.callPost("agency", updates, globalState);
        handleNotificationMessage(`Agency ${isNew ? "created" : "updated"} successfully`, "success");
        await loadAgencies();
      } catch (err) {
        handleNotificationMessage(err, "error");
      }
    },
    [getApi, globalState, loadAgencies]
  );

  const handleSave = useCallback((agency) => AddUpdateAgency({ ...agency, is_active: true }), [AddUpdateAgency]);

  const handleDelete = useCallback(
    async (row) => {
      confirmDialogActions.confirm({
        title: "Delete this entry?",
        prompt: "Are you sure you want to delete this entry?",
        promptDetail: null,
        callback: () => {
          AddUpdateAgency({ ...row, is_active: false });
        },
      });
    },
    [AddUpdateAgency, confirmDialogActions]
  );

  const filteredRows = useMemo(() => {
    if (!searchTerm) return rows;
    const lower = searchTerm.toLowerCase();
    return rows.filter((r) => r.name.toLowerCase().includes(lower));
  }, [rows, searchTerm]);

  useEffect(() => {
    addAttachments([{ name: "Agencies", content: filteredRows }]);
  }, [filteredRows]);

  const columns = useMemo(
    () => [
      { field: "name", headerName: "Name", flex: 1, minWidth: 200 },
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        headerAlign: "right",
        align: "right",
        width: 140,
        getActions: (params) => [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            label="Edit"
            onClick={() => setAddEditAgency(params.row)}
          />,
          ...(globalState.user_role === USER_ROLE_ADMIN
            ? [
                <GridActionsCellItem
                  key="delete"
                  icon={<DeleteIcon />}
                  label="Delete"
                  onClick={() => handleDelete(params.row)}
                />,
              ]
            : []),
          <GridActionsCellItem
            key="navigate"
            icon={<NavigateIcon />}
            label="View Details"
            onClick={() =>
              navigate(`/system/agencies/${params.row.id}`, {
                state: {
                  name: params.row.name,
                },
              })
            }
          />,
        ],
      },
    ],
    [handleDelete, navigate]
  );

  return (
    <>
      <WorkspaceHeader
        title="Agencies"
        category={{
          title: "System",
          icon: <SystemIcon />,
        }}
      />
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            placeholder="Search agencies..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end">
                    <SearchOutlined />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>

      <Box sx={{ height: 600, width: "100%" }}>
        <TilleyDataGrid
          rows={filteredRows}
          columns={columns}
          pageSizeOptions={[5, 10, 15, 20, 25, 50, 100]}
          defaultPageSize={10}
          getRowId={(r) => r.id}
          isLoading={isLoading}
        />
      </Box>

      {globalState.user_role === USER_ROLE_ADMIN && (
        <WorkspaceActionBar
          content={
            <Box>
              <Button
                variant="contained"
                sx={{ width: "100%" }}
                startIcon={<AddCircleOutline />}
                onClick={() => setAddEditAgency({})}
              >
                New Agency
              </Button>
            </Box>
          }
        />
      )}

      <Dialog open={Boolean(isAddEditAgency)} onClose={() => setAddEditAgency(null)}>
        <AgencyAddEditDialog
          addEditAgency={isAddEditAgency}
          onChange={handleSave}
          onClose={() => setAddEditAgency(null)}
        />
      </Dialog>
      {confirmDialogComponent}
    </>
  );
}
